import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { BarChart3, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

export function AnalyticsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const analyticsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'analytics');
  const isEnabled = analyticsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'analytics') : pluginApi.disablePlugin(serverId, 'analytics'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'analytics', config),
    {
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    trackMessages: true,
    trackVoice: true,
    trackReactions: true,
    trackInvites: true,
    retentionDays: 30,
    exportEnabled: false
  });

  useEffect(() => {
    if (analyticsPlugin?.config) {
      setConfig({ ...config, ...analyticsPlugin.config });
    }
  }, [analyticsPlugin]);

  if (!analyticsPlugin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>Plugin non disponibile per questo server.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-500/20 rounded-xl">
            <BarChart3 className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Analytics</h3>
            <p className="text-sm text-muted-foreground">Metriche di attività e trend del server.</p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Retention (giorni)</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={config.retentionDays}
                  onChange={(e) => setConfig({ ...config, retentionDays: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Export CSV</Label>
                  <p className="text-xs text-muted-foreground">Consenti esportazione dati</p>
                </div>
                <Switch checked={config.exportEnabled} onCheckedChange={(checked) => setConfig({ ...config, exportEnabled: checked })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Messaggi</Label>
                  <p className="text-xs text-muted-foreground">Traccia volume messaggi</p>
                </div>
                <Switch checked={config.trackMessages} onCheckedChange={(checked) => setConfig({ ...config, trackMessages: checked })} />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Voice</Label>
                  <p className="text-xs text-muted-foreground">Traccia tempo in voice</p>
                </div>
                <Switch checked={config.trackVoice} onCheckedChange={(checked) => setConfig({ ...config, trackVoice: checked })} />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Reactions</Label>
                  <p className="text-xs text-muted-foreground">Traccia reazioni</p>
                </div>
                <Switch checked={config.trackReactions} onCheckedChange={(checked) => setConfig({ ...config, trackReactions: checked })} />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Inviti</Label>
                  <p className="text-xs text-muted-foreground">Traccia inviti e referral</p>
                </div>
                <Switch checked={config.trackInvites} onCheckedChange={(checked) => setConfig({ ...config, trackInvites: checked })} />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
